import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useTranslation } from '../../utils/i18n'
import { generateServiceNavigation } from '../../utils/navigation/serviceNavigation'
import { companyProfile } from '@/config/companyProfile'
import { useAuth } from '@/contexts/AuthContext'
import MegaMenu from './MegaMenu'
import './Header.css'
import './MegaMenu.css'

const languages = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pt-BR', label: 'Português (Brasil)', flag: '🇧🇷' }
]

export default function Header () {
  const { t, changeLanguage, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [megaOpen, setMegaOpen] = useState(false)
  const [isTouchUi, setIsTouchUi] = useState(false)
  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const langMenuRef = useRef(null)
  const megaCloseTimeoutRef = useRef(null)
  const serviceNavigation = useMemo(() => generateServiceNavigation(t), [t, i18n.language])
  const logoInitial = useMemo(() => companyProfile.name.charAt(0), [])
  const logoSrc = '/assets/brand/logo-serenita-mark-cropped.png'
  const currentLanguage = useMemo(() => {
    return languages.find(lang => lang.code === i18n.language) || languages[0]
  }, [i18n.language])
  const userInitials = useMemo(() => {
    if (!user?.email) return ''
    return user.email.slice(0, 2).toUpperCase()
  }, [user])

  const cancelMegaClose = () => {
    if (megaCloseTimeoutRef.current) {
      clearTimeout(megaCloseTimeoutRef.current)
      megaCloseTimeoutRef.current = null
    }
  }

  const scheduleMegaClose = () => {
    cancelMegaClose()
    megaCloseTimeoutRef.current = setTimeout(() => {
      setMegaOpen(false)
      megaCloseTimeoutRef.current = null
    }, 250)
  }

  useEffect(() => {
    return () => cancelMegaClose()
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const media = window.matchMedia('(hover: none), (pointer: coarse)')
    const update = () => setIsTouchUi(Boolean(media.matches))
    update()
    if (media.addEventListener) {
      media.addEventListener('change', update)
      return () => media.removeEventListener('change', update)
    }
    media.addListener(update)
    return () => media.removeListener(update)
  }, [])

  const closeMenus = () => {
    cancelMegaClose()
    setMegaOpen(false)
    setMobileOpen(false)
  }

  useEffect(() => {
    document.body.classList.toggle('no-scroll', mobileOpen)
    return () => {
      document.body.classList.remove('no-scroll')
    }
  }, [mobileOpen])

  useEffect(() => {
    if (!langMenuOpen) return
    const onPointerDown = (event) => {
      const container = langMenuRef.current
      if (!container) return
      if (container.contains(event.target)) return
      setLangMenuOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [langMenuOpen])

  return (
    <>
      <header className="site-header">
      <div className="container header-content">
        <Link to="/" className="logo" onClick={() => setMobileOpen(false)}>
          {!logoFailed
            ? (
              <img
                className="brand-logo"
                src={logoSrc}
                alt={companyProfile.name}
                loading="eager"
                decoding="async"
                onError={() => setLogoFailed(true)}
              />
              )
            : (
              <div className="logo-mark" aria-hidden="true">
                <span className="logo-fallback" aria-hidden="true">{logoInitial}</span>
              </div>
              )}
          <span>{companyProfile.name}</span>
        </Link>

        <nav className={`main-nav ${mobileOpen ? 'is-open' : ''}`}>

          <NavLink to="/" end onClick={closeMenus}>
            {t('nav.home')}
          </NavLink>
          <div
            className={`nav-dropdown ${megaOpen ? 'is-open' : ''}`}
            onMouseEnter={() => {
              if (isTouchUi) return
              cancelMegaClose()
              setMegaOpen(true)
            }}
            onMouseLeave={() => {
              if (isTouchUi) return
              scheduleMegaClose()
            }}
          >
            <button
              type="button"
              className="nav-dropdown-trigger"
              aria-expanded={megaOpen}
              onClick={() => {
                cancelMegaClose()
                setMegaOpen(prev => !prev)
              }}
            >
              <span className="nav-dropdown-trigger__label">{t('nav.services')}</span>
            </button>
            <MegaMenu sections={serviceNavigation} onNavigate={closeMenus} />
          </div>
          <NavLink to="/courses" onClick={closeMenus}>
            {t('nav.courses')}
          </NavLink>
          <NavLink to="/about" onClick={closeMenus}>
            {t('nav.about')}
          </NavLink>
          <NavLink to="/contact" onClick={closeMenus}>
            {t('nav.contact')}
          </NavLink>
          <NavLink to="/quote" className="nav-cta" onClick={closeMenus}>
            {t('nav.quote')}
          </NavLink>
        </nav>

        <div className="header-actions">
          <div className="language-dropdown" ref={langMenuRef}>
            <button
              type="button"
              className="language-dropdown__trigger"
              aria-haspopup="menu"
              aria-expanded={langMenuOpen}
              aria-label={t('header.language_label', 'Changer de langue')}
              onClick={() => setLangMenuOpen(prev => !prev)}
            >
              <span role="img" aria-hidden="true">{currentLanguage.flag}</span>
              <span className="language-dropdown__caret" aria-hidden="true">▾</span>
            </button>
            {langMenuOpen && (
              <div className="language-dropdown__menu" role="menu">
                {languages.map(lang => (
                  <button
                    key={lang.code}
                    type="button"
                    role="menuitemradio"
                    className={i18n.language === lang.code ? 'active' : ''}
                    aria-checked={i18n.language === lang.code}
                    onClick={() => {
                      changeLanguage(lang.code)
                      setLangMenuOpen(false)
                    }}
                  >
                    <span role="img" aria-hidden="true">{lang.flag}</span>
                    <span className="language-dropdown__label">{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="language-switcher" role="group" aria-label={t('header.language_label', 'Changer de langue')}>
            {languages.map(lang => (
              <button
                key={lang.code}
                type="button"
                className={i18n.language === lang.code ? 'active' : ''}
                aria-label={lang.label}
                aria-pressed={i18n.language === lang.code}
                onClick={() => changeLanguage(lang.code)}
              >
                <span role="img" aria-hidden="true">{lang.flag}</span>
              </button>
            ))}
          </div>
          {user
            ? (
            <div className="user-chip">
              <Link
                to="/portal"
                className="user-chip__btn"
                onClick={closeMenus}
                aria-label={t('header.account_aria', 'Ouvrir l’espace étudiant')}
              >
                <span className="user-chip__avatar">{userInitials}</span>
                <span className="user-chip__label">{t('header.account', 'Compte')}</span>
              </Link>
              <button type="button" className="user-chip__logout" onClick={() => { logout(); closeMenus() }}>
                {t('header.logout', 'Sortir')}
              </button>
            </div>
              )
            : (
            <Link to="/login" className="login-btn" onClick={closeMenus}>
              {t('nav.login')}
            </Link>
              )}
          <button
            className={`menu-toggle ${mobileOpen ? 'is-open' : ''}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-expanded={mobileOpen}
            aria-label={mobileOpen ? t('header.menu_close', 'Fermer le menu') : t('header.menu_open', 'Ouvrir le menu')}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
      {mobileOpen && <div className="nav-overlay" onClick={closeMenus} aria-hidden="true" />}
    </>
  )
}
